
import { empty, fold, singleton, mapIndexed, length, isEmpty, filter, map, choose, tryFind } from "../../fable_modules/fable-library-js.5.13.0/List.js";
import { compare, safeHash, equals } from "../../fable_modules/fable-library-js.5.13.0/Util.js";
import { ofInstanceCached } from "../../Simulator/FastSim/PortView.js";
import { List_distinctBy } from "../../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { contains, ofList } from "../../fable_modules/fable-library-js.5.13.0/Set.js";
import { needFetching, holdNothing, setLocal, current, CachedWave, setFetched, setNoDriver } from "./WaveData.js";
import { dbg, warnOnce } from "../../Shared/Log.js";
import { FastSimulation__get_SimulatedTopSheet } from "../../Simulator/SimTypes.js";
import { FSharpResult$2 } from "../../fable_modules/fable-library-js.5.13.0/Result.js";
import { PromiseBuilder__Delay_62FBFDE1, PromiseBuilder__Run_212F1D4B } from "../../fable_modules/Fable.Promise.3.1.3/Promise.fs.js";
import { promise } from "../../fable_modules/Fable.Promise.3.1.3/PromiseImpl.fs.js";
import { transportFailure, errorOfFrame, simReadDrivers } from "../../Interface/SidecarClient.js";
import { fromZero, fromUInt32, op_LeftShift, op_Addition } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { item } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { toList } from "../../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../../fable_modules/fable-library-js.5.13.0/Range.js";

function widthOf(fs, wi) {
    const patternInput = wi.Id;
    let option_3;
    const option_1 = tryFind((p) => {
        if (equals(p.PortComp, patternInput[0]) && equals(p.PortIs, wi.PortType)) {
            return p.PortNum === wi.PortNumber;
        }
        else {
            return false;
        }
    }, ofInstanceCached(fs, patternInput[1]).ViewPorts);
    option_3 = ((option_1 != null) ? option_1.PortWidth : undefined);
    if (option_3 != null) {
        if (option_3 > 0) {
            return option_3;
        }
        else {
            return undefined;
        }
    }
    else {
        return undefined;
    }
}

function fetchWaves(epoch, fs, waves, window$) {
    let this$;
    const asked = List_distinctBy((wi) => wi.SimArrayIndex, waves, {
        Equals: equals,
        GetHashCode: (x) => (safeHash(x) | 0),
    });
    const wanted = choose((wi_1) => {
        const option_1 = widthOf(fs, wi_1);
        if (option_1 != null) {
            return [wi_1.SimArrayIndex, option_1];
        }
        else {
            return undefined;
        }
    }, asked);
    let unnameable;
    const named = ofList(map((tuple) => tuple[0], wanted), {
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    });
    unnameable = filter((i) => !contains(i, named), map((wi_2) => wi_2.SimArrayIndex, asked));
    if (!isEmpty(unnameable)) {
        setNoDriver(epoch, unnameable, window$);
        const plural = (length(unnameable) === 1) ? "waveform" : "waveforms";
        warnOnce(`no-driver-${FastSimulation__get_SimulatedTopSheet(fs)}-${length(unnameable)}`, ((`${length(unnameable)} of ${length(asked)} ${plural} cannot be read from the .NET simulator:`) + " nothing drives them, which is what an unconnected input port is.") + " They are left blank; every other waveform is unaffected.");
    }
    if (isEmpty(wanted)) {
        return Promise.resolve(new FSharpResult$2(/* Ok */ 0, [undefined]));
    }
    else {
        const leadIn = window$.StartSample > 0;
        let firstCycle;
        if (leadIn) {
            firstCycle = (((this$ = window$, this$.StartSample * this$.Multiplier)) - window$.Multiplier);
        }
        else {
            const this$_1 = window$;
            firstCycle = (this$_1.StartSample * this$_1.Multiplier);
        }
        const samples = (window$.SampleCount + (leadIn ? 1 : 0)) | 0;
        const requested = map((tuple_1) => tuple_1[0], wanted);
        return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => (simReadDrivers(epoch, firstCycle, window$.Multiplier, samples, requested).then((_arg) => {
            const frame = _arg;
            const matchValue = errorOfFrame(frame);
            if (matchValue == null) {
                const wordsPerSample = (new DataView(frame.buffer, frame.byteOffset).getUint32(16, true)) | 0;
                const data = new Uint32Array(frame.buffer, frame.byteOffset + 24, ((length(requested) * samples) * wordsPerSample));
                setFetched(epoch, mapIndexed((row, tupledArg) => [tupledArg[0], new CachedWave(window$, tupledArg[1], leadIn, wordsPerSample, (row * samples) * wordsPerSample, data)], wanted));
                return Promise.resolve(new FSharpResult$2(/* Ok */ 0, [undefined]));
            }
            else {
                const e = matchValue;
                return Promise.resolve(new FSharpResult$2(/* Error */ 1, [e]));
            }
        }))));
    }
}

/**
 * One signal's value at one cycle: what the schematic probe shows.
 * 
 * A read like any other - the same by-handle read, one signal and one sample of it - so the probe uses
 * the mechanism everything else uses rather than a transport of its own. It was briefly a
 * BLOCKING read over a second transport, so that the value could be had inside the render that
 * draws it. That cost 2.2ms against this path's 0.2ms, because a synchronous XMLHttpRequest is
 * the only thing that can block a renderer's thread and Chromium does not make it quick. The
 * label appearing one render later is not something a user can see.
 */
export function fetchProbeValue(epoch, fs, wi, cycle) {
    const matchValue = widthOf(fs, wi);
    if (matchValue != null) {
        return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => (simReadDrivers(epoch, cycle, 1, 1, singleton(wi.SimArrayIndex)).then((_arg) => {
            const frame = _arg;
            const matchValue_1 = errorOfFrame(frame);
            if (matchValue_1 == null) {
                const wordsPerSample = (new DataView(frame.buffer, frame.byteOffset).getUint32(16, true)) | 0;
                const data = new Uint32Array(frame.buffer, frame.byteOffset + 24, wordsPerSample);
                return Promise.resolve(fold((acc, w) => op_Addition(op_LeftShift(acc, 32), fromUInt32(item(w, data))), fromZero(), toList(rangeDouble(wordsPerSample - 1, -1, 0))));
            }
            else {
                dbg(64, `reading the probe's wire at cycle ${cycle}: ${matchValue_1}`);
                return Promise.resolve(undefined);
            }
        }))));
    }
    else {
        return Promise.resolve(undefined);
    }
}

function fetchForView(epoch, fs, waves, window$) {
    return fetchWaves(epoch, fs, waves, window$);
}

/**
 * Choose the simulator for this refresh, and say what the renderer's own one reads through.
 * 
 * Called once per refresh so that nothing below has to branch on which simulator is running.
 * 
 * **The session decides, and nothing else has to.** This took a `newSimulation` flag from the
 * caller as well, which said the design or its shape had changed and so what was cached had been
 * read from a simulation that no longer exists. That is exactly what a different epoch says, and
 * the epoch says it without anyone having to remember to pass it: a build issues a new one, so a
 * cache of the old one is emptied here on the next refresh whether or not the refresh knew it was
 * the first after a build. `epoch` is 0 when there is no session, which is a session number no
 * build ever issues, so the cache of a stopped simulation is not mistaken for a live one either.
 */
export function selectSimulator(inRenderer, epoch, localLookup, localClock) {
    const matchValue = current();
    let matchResult, held_1;
    if (inRenderer) {
        matchResult = 0;
    }
    else if (matchValue.tag === 1) {
        if (matchValue.fields[0] === epoch) {
            matchResult = 1;
            held_1 = matchValue.fields[0];
        }
        else {
            matchResult = 2;
        }
    }
    else {
        matchResult = 2;
    }
    switch (matchResult) {
        case 0: {
            setLocal(localLookup, localClock);
            break;
        }
        case 1: {
            break;
        }
        case 2: {
            holdNothing(epoch);
            break;
        }
    }
}

/**
 * How many cycles the simulation being shown has actually been run for.
 * 
 * Whichever simulator is running is the one that knows. Asking the renderer's FastSimulation
 * while the sidecar simulates gives zero however far the sidecar has gone, which is not a number
 * anything should act on: it put the cursor back to cycle 0 when a progress bar was cancelled.
 */
export function cyclesSimulated(inRenderer, sidecarClock, fs) {
    if (inRenderer) {
        return fs.ClockTick | 0;
    }
    else {
        return sidecarClock | 0;
    }
}

/**
 * The waves that are not holding the window they are about to be drawn over, and so have to be
 * asked for.
 * 
 * Derived, on every refresh, from what the cache holds and what the view asks for. Nothing records
 * which waves are outstanding, because nothing needs to: a wave needs fetching exactly when it has
 * not got the cycles it is being drawn over.
 * 
 * None of them, ever, when the renderer is simulating: its cache reads through to step arrays that
 * are already in memory, so there is nothing to fetch and nothing to wait for. That asymmetry is
 * the only thing that distinguishes the two simulators here, and it is why the caller needs no
 * flag of its own.
 */
export function wavesToFetch(inRenderer, handles, window$) {
    if (inRenderer) {
        return empty();
    }
    else {
        return needFetching(handles, window$);
    }
}

/**
 * Fetch some waves over the window they are drawn over, and put them where the viewer reads them.
 * 
 * **One fetch at a time**, which the CALLER enforces with `WaveSimModel.FetchInProgress`. A fetch
 * is asked for whenever a wave is not holding its window - every checkbox tick and scroll step -
 * so without that a second chain starts while the first is still running, and the two interleave
 * build, run and read against one session: the second chain's build resets the simulation under
 * the first chain's read. The sidecar serves them in arrival order and cannot tell that they
 * belong to different views.
 * 
 * One request covers every wave asked for, so the number of round trips is one per view rather
 * than one per wave, and the waves that arrive together are drawable together.
 */
export function fetchWavesFor(epoch, fs, waves, window$) {
    const pr = fetchForView(epoch, fs, waves, window$);
    return pr.catch((e) => (new FSharpResult$2(/* Error */ 1, [transportFailure(e.message)])));
}

